// src/pages/Login.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axiosInstance from "../api/axiosInstance";
import { FiArrowLeft, FiEye, FiEyeOff } from "react-icons/fi";
import "./cx.css"; // theme tokens (--accent ...)
import "./auth.css";
import notify from "../utils/notify";

/**
 * Stable per-browser device id to support "new device" detection on the backend.
 * - Stored in localStorage.
 * - Sent to API as X-Device-Id.
 */
const DEVICE_ID_KEY = "nnetego_device_id";

const getOrCreateDeviceId = () => {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);

    if (!id) {
      id =
        (window.crypto &&
          typeof window.crypto.randomUUID === "function" &&
          window.crypto.randomUUID()) ||
        `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random()
          .toString(16)
          .slice(2)}`;

      localStorage.setItem(DEVICE_ID_KEY, id);
    }

    return id;
  } catch {
    // Storage blocked (private mode / strict policies)
    return `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random()
      .toString(16)
      .slice(2)}`;
  }
};

const isJwtLike = (t) => typeof t === "string" && t.split(".").length === 3 && t.length > 20;

const normalizeEmail = (v) => String(v || "").trim().toLowerCase();

const isValidEmail = (v) => {
  // Lightweight check; keep backend as source of truth
  const s = normalizeEmail(v);
  return s.length >= 5 && s.includes("@") && s.includes(".");
};

const parseRetryAfterSeconds = (error) => {
  const ra = error?.response?.headers?.["retry-after"];
  if (!ra) return null;
  const n = Number(ra);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { handleLogin } = useAuth();

  const [email, setEmail] = useState(normalizeEmail(location.state?.email || ""));
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [rateLimitMessage, setRateLimitMessage] = useState("");

  // cooldown for 429 responses
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const cooldownTimerRef = useRef(null);

  const deviceId = useMemo(() => getOrCreateDeviceId(), []);

  // Prevent double-submit even before React re-renders
  const submitLockRef = useRef(false);

  // Cancel in-flight request if needed
  const abortRef = useRef(null);

  // Guard state updates after unmount (prevents "blink/reload feel")
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      try {
        abortRef.current?.abort?.();
      } catch {
        // ignore
      }
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (location.state?.email) {
      setEmail(normalizeEmail(location.state.email));
    }
  }, [location.state]);

  const clearMessages = useCallback(() => {
    if (errorMessage) setErrorMessage("");
    if (rateLimitMessage) setRateLimitMessage("");
  }, [errorMessage, rateLimitMessage]);

  const startCooldown = useCallback((seconds) => {
    const s = Math.max(1, Number(seconds) || 60);

    setCooldownSeconds(s);
    setRateLimitMessage(`Too many attempts. Please wait ${s} seconds before trying again.`);

    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);

    cooldownTimerRef.current = setInterval(() => {
      setCooldownSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownTimerRef.current);
          cooldownTimerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const validateInputs = useCallback(() => {
    const e = normalizeEmail(email);
    if (!e || !password) {
      setErrorMessage("Please enter both email and password.");
      return false;
    }
    if (!isValidEmail(e)) {
      setErrorMessage("Please enter a valid email address.");
      return false;
    }
    return true;
  }, [email, password]);

  const getRedirectPath = useCallback((role) => {
    switch (role) {
      case "SystemAdmin":
      case "Admin":
        return "/admin/dashboard";
      case "Manager":
        return "/manager/dashboard";
      case "Receptionist":
        return "/receptionist/dashboard";
      case "Housekeeper":
        return "/housekeeper/dashboard";
      case "Maintenance":
        return "/maintenance/dashboard";
      case "Owner":
      case "User":
        return "/hotels/dashboard";
      default:
        return "/hotels/dashboard";
    }
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Prevent double-submit
      if (submitLockRef.current) return;

      clearMessages();

      // Don't allow attempts during cooldown
      if (cooldownSeconds > 0) {
        setRateLimitMessage(`Please wait ${cooldownSeconds} seconds before trying again.`);
        return;
      }

      if (!validateInputs()) return;

      submitLockRef.current = true;
      setIsLoading(true);

      // Cancel any previous in-flight login request
      try {
        abortRef.current?.abort?.();
      } catch {
        // ignore
      }
      abortRef.current = new AbortController();

      const safeEmail = normalizeEmail(email);

      try {
        const response = await axiosInstance.post(
          "/Auth/login",
          { email: safeEmail, password },
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-Device-Id": deviceId,
            },
            withCredentials: true,
            signal: abortRef.current.signal,
          }
        );

        const accessToken = response?.data?.accessToken;

        // Critical: prevent bad auth state like token=true or empty tokens
        if (response.status === 200 && isJwtLike(accessToken)) {
          handleLogin(response.data, { silent: true }); // this page shows the one welcome toast below

          const role =
            (Array.isArray(response.data.user?.role)
              ? response.data.user.role[0]
              : response.data.user?.role) || "User";

          // Optional: if you use "return to where you came from", support it here:
          const from = location.state?.from?.pathname;
          navigate(from || getRedirectPath(role), { replace: true });

          notify.success("Logged in successfully!");
          return;
        }

        setErrorMessage("Unexpected response from server. Please try again.");
      } catch (error) {
        // If we aborted the request, do nothing
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
          return;
        }

        console.error("Login error:", error);

        const status = error?.response?.status;
        const apiError = error?.response?.data?.error;
        const apiMessage = error?.response?.data?.message;

        if (apiError === "EmailNotConfirmed") {
          navigate("/email-confirmation-pending", {
            state: {
              email: safeEmail,
              message: "Please confirm your email address before logging in.",
            },
            replace: true,
          });
          return;
        }

        // Account locked
        if (status === 423) {
          setErrorMessage(apiMessage || "Your account is temporarily locked. Please try again later.");
          return;
        }

        // Rate limiting
        if (status === 429) {
          const retryAfter = parseRetryAfterSeconds(error);
          startCooldown(retryAfter || 60);
          return;
        }

        // Unauthorized (wrong credentials)
        if (status === 401) {
          setErrorMessage(apiMessage || "Invalid email or password.");
          return;
        }

        setErrorMessage(apiMessage || "Login failed. Please try again.");
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
        }
        submitLockRef.current = false;
      }
    },
    [
      abortRef,
      clearMessages,
      cooldownSeconds,
      deviceId,
      email,
      getRedirectPath,
      handleLogin,
      location.state,
      navigate,
      password,
      startCooldown,
      validateInputs,
    ]
  );

  const onEmailChange = (val) => {
    setEmail(normalizeEmail(val));
    clearMessages();
  };

  const onPasswordChange = (val) => {
    setPassword(val);
    clearMessages();
  };

  const isSubmitDisabled = isLoading || cooldownSeconds > 0;

  return (
    <div className="auth-page">
      <div className="auth-card">
        <header className="auth-brand">
          <Link to="/" aria-label="Nnetego home">
            <img src="/images/logo.png" alt="Nnetego" />
          </Link>
          <h1>Welcome back</h1>
          <p>Sign in to your Nnetego HMS account.</p>
        </header>

        {errorMessage && (
          <div className="auth-alert is-error" role="alert" aria-live="assertive">
            {errorMessage}
          </div>
        )}

        {rateLimitMessage && (
          <div className="auth-alert is-warn" role="alert" aria-live="polite">
            {rateLimitMessage}
            {cooldownSeconds > 0 ? ` (${cooldownSeconds}s)` : ""}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="auth-field">
            <label htmlFor="login-email">Email</label>
            <input
              id="login-email"
              className="auth-input"
              type="email"
              placeholder="you@yourhotel.com"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              disabled={isLoading}
              required
              autoComplete="username"
              inputMode="email"
              name="email"
            />
          </div>

          <div className="auth-field">
            <div className="auth-label-row">
              <label htmlFor="login-password">Password</label>
              <Link to="/forgot-password">Forgot password?</Link>
            </div>
            <div className="auth-password">
              <input
                id="login-password"
                className="auth-input"
                type={showPassword ? "text" : "password"}
                placeholder="Your password"
                value={password}
                onChange={(e) => onPasswordChange(e.target.value)}
                disabled={isLoading}
                required
                autoComplete="current-password"
                name="password"
              />
              {/* a real button so it never submits the form */}
              <button
                type="button"
                className="auth-eye"
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword((s) => !s)}
                disabled={isLoading}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </div>

          <button type="submit" className="auth-btn auth-btn--block" disabled={isSubmitDisabled}>
            {isLoading ? "Signing in..." : cooldownSeconds > 0 ? `Wait ${cooldownSeconds}s` : "Sign in"}
          </button>
        </form>

        <div className="auth-divider">New to Nnetego?</div>
        <Link to="/register" className="auth-btn auth-btn--outline auth-btn--block">
          Create an account
        </Link>

        <p className="auth-back">
          <Link to="/">
            <FiArrowLeft /> Back to home
          </Link>
        </p>

        <footer className="auth-foot">
          © {new Date().getFullYear()} Nnetego. All rights reserved. <Link to="/legal/privacy">Privacy</Link> · <Link to="/legal/terms">Terms</Link>
        </footer>
      </div>
    </div>
  );
};

export default Login;
